/**
 * Room placement geometry: where a whole room sits in the whole-property
 * floor plan (the assembler). Mirrors the free-element rotation family
 * (`freeElementGeometry.ts`): `x`/`y` place the room's local origin, `rotation`
 * turns it about the primary rectangle's center, and every assembly-space
 * affordance is authored in room-local coordinates and projected through
 * these, so a rotated room reads exactly like an axis-aligned one.
 */
import { alcoveDepth, type EdgeName, edgeSegments, type FloorPlanRoom, type Point, ROOM_FIELD_DEFAULTS } from '../schemas';
import { degreesToRadians, rotateAbout, rotateVector } from './rotation';

/** The level a room with no `level` is on: the first floor. */
export const DEFAULT_ROOM_LEVEL = 1;

export interface RoomPlacement {
  /** Building level: 0 = basement, 1 = first floor, ... */
  level: number;
  /** Whole-property plan position of the room's local origin, feet. */
  x: number;
  y: number;
  /** Floor elevation, feet. */
  z: number;
  /** Plan rotation about the primary rectangle's center, degrees. */
  rotation: number;
}

export interface PlanBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/**
 * Room extent including alcove depths - the architectural footprint, and the
 * span free-element positions (and whole-room placements in the assembler)
 * are measured against. Deliberately narrower than `geometryBounds`, which
 * also counts out-swinging door clearance.
 */
export function alcoveExtendedBounds(room: FloorPlanRoom): PlanBounds {
  const maxAlcoveDepth = (edge: EdgeName): number =>
    edgeSegments(room, edge).reduce(
      (max, segment) => segment.edgeSegmentType === 'alcove' ? Math.max(max, alcoveDepth(segment)) : max,
      0,
    );
  // `0 - depth` rather than `-depth`: a room with no alcoves gets a clean +0, not -0.
  return {
    minX: 0 - maxAlcoveDepth('leftEdge'),
    maxX: room.width + maxAlcoveDepth('rightEdge'),
    minY: 0 - maxAlcoveDepth('backEdge'),
    maxY: room.length + maxAlcoveDepth('frontEdge'),
  };
}

export const roomLevel = (room: FloorPlanRoom): number => room.level ?? DEFAULT_ROOM_LEVEL;

/** A room is placed once it has an assembly position; level/z/rotation alone don't count. */
export const isRoomPlaced = (room: FloorPlanRoom): boolean => room.x !== undefined && room.y !== undefined;

/** The room's placement with every documented default filled in. */
export const roomPlacement = (room: FloorPlanRoom): RoomPlacement => ({
  level: roomLevel(room),
  x: room.x ?? 0,
  y: room.y ?? 0,
  z: room.z ?? ROOM_FIELD_DEFAULTS.z,
  rotation: room.rotation ?? ROOM_FIELD_DEFAULTS.rotation,
});

/** The room's plan rotation in radians (0 when unset). */
export const roomPlacementRadians = (room: FloorPlanRoom): number => degreesToRadians(room.rotation ?? ROOM_FIELD_DEFAULTS.rotation);

/**
 * Room-local plan point → whole-property plan point: rotate about the primary
 * rectangle's center, then translate the local origin to (x, y). `inverse`
 * maps the other way (assembly → room-local). Cosine/sine computed once.
 */
export function roomPlacementTransform(room: FloorPlanRoom, inverse = false): (point: Point) => Point {
  const { x, y, rotation } = roomPlacement(room);
  const radians = degreesToRadians(rotation);
  if (!radians) {
    if (!x && !y) return point => point;
    return inverse
      ? point => ({ x: point.x - x, y: point.y - y })
      : point => ({ x: point.x + x, y: point.y + y });
  }
  const cosine = Math.cos(radians), sine = Math.sin(radians);
  const center = { x: room.width / 2, y: room.length / 2 };
  if (inverse) return point => rotateAbout({ x: point.x - x, y: point.y - y }, center, cosine, -sine);
  const placedCenter = { x: x + center.x, y: y + center.y };
  return point => rotateAbout(point, center, cosine, sine, placedCenter);
}

/** Rotate a room-local direction vector into the whole-property plan (no translation). */
export function roomPlacementRotateVector(room: FloorPlanRoom, vector: Point): Point {
  return rotateVector(vector, roomPlacementRadians(room));
}

/** The room's rotated architectural extent (alcoves included, door swings excluded) as an
 *  axis-aligned box in the whole-property plan: the box the assembler measures and fits to. */
export function roomAssemblyBounds(room: FloorPlanRoom): PlanBounds {
  const local = alcoveExtendedBounds(room);
  const toPlan = roomPlacementTransform(room);
  const corners = [
    toPlan({ x: local.minX, y: local.minY }),
    toPlan({ x: local.maxX, y: local.minY }),
    toPlan({ x: local.maxX, y: local.maxY }),
    toPlan({ x: local.minX, y: local.maxY }),
  ];
  return {
    minX: Math.min(...corners.map(c => c.x)),
    minY: Math.min(...corners.map(c => c.y)),
    maxX: Math.max(...corners.map(c => c.x)),
    maxY: Math.max(...corners.map(c => c.y)),
  };
}

/** A box grown by `margin` on every side (the wall band around a measured perimeter, say). */
export const expandPlanBounds = (bounds: PlanBounds, margin: number): PlanBounds => ({
  minX: bounds.minX - margin,
  minY: bounds.minY - margin,
  maxX: bounds.maxX + margin,
  maxY: bounds.maxY + margin,
});

/** The union of several boxes, or null when there are none. */
export function unionPlanBounds(boxes: readonly PlanBounds[]): PlanBounds | null {
  if (!boxes.length) return null;
  return boxes.reduce((acc, b) => ({
    minX: Math.min(acc.minX, b.minX),
    minY: Math.min(acc.minY, b.minY),
    maxX: Math.max(acc.maxX, b.maxX),
    maxY: Math.max(acc.maxY, b.maxY),
  }));
}
